/*───────────────────────────────────────────────────────────────
 📄 File: applyTheme.ts
 🧩 Description:
    DOM-side theme application function. Reads themePrefs and tenantTheme
    and sets CSS custom properties on document.documentElement.
    Extracted from store/theme.ts.
────────────────────────────────────────────────────────────────*/

import { ThemeMode, ThemePrefs } from './theme';
import { hexToHsl, getContrastColor, hexToRgbComma } from './themeColorUtils';
import { DARK_VARIANT_COLORS, LIGHT_VARIANT_COLORS } from './themeVariantColors';

interface TenantTheme { primaryColor: string; secondaryColor: string; }

export function applyTheme(
    themeMode: ThemeMode,
    themePrefs: ThemePrefs,
    tenantTheme: TenantTheme,
): void {
    // Resolve actual mode (system → detect via matchMedia)
    let actualMode = themePrefs?.mode || themeMode;
    if (actualMode === 'system') {
        actualMode = window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
    }

    const root = document.documentElement;

    if (actualMode === 'dark') {
        root.classList.add('dark');
        root.classList.remove('light');

        const variant = themePrefs.darkModeVariant || 'very-dark-gray';
        const colors = DARK_VARIANT_COLORS[variant] ?? DARK_VARIANT_COLORS['very-dark-gray'];
        root.style.setProperty('--background', hexToHsl(colors.bg));
        root.style.setProperty('--card', hexToHsl(colors.card));
        root.style.setProperty('--popover', hexToHsl(colors.card));
        root.style.setProperty('--muted', hexToHsl(colors.card));
        root.style.setProperty('--accent', hexToHsl(colors.card));
        root.style.setProperty('--border', hexToHsl(colors.border));
        root.style.setProperty('--input', hexToHsl(colors.border));
        root.style.setProperty('--sidebar', hexToHsl(colors.sidebar));
        root.style.setProperty('--sidebar-accent', hexToHsl(colors.card));
        root.style.setProperty('--sidebar-border', hexToHsl(colors.border));
        root.style.setProperty('--foreground', '210 40% 98%');
        root.style.setProperty('--card-foreground', '210 40% 98%');
        root.style.setProperty('--popover-foreground', '210 40% 98%');
        root.style.setProperty('--muted-foreground', '215 20.2% 65.1%');
        root.style.setProperty('--accent-foreground', '210 40% 98%');
        root.style.setProperty('--sidebar-foreground', '220 9% 85%');
        root.style.setProperty('--sidebar-accent-foreground', '220 9% 95%');
    } else {
        root.classList.remove('dark');
        root.classList.add('light');

        const variant = themePrefs.lightModeVariant || 'white';
        const colors = LIGHT_VARIANT_COLORS[variant] ?? LIGHT_VARIANT_COLORS['white'];
        root.style.setProperty('--background', hexToHsl(colors.bg));
        root.style.setProperty('--card', hexToHsl(colors.card));
        root.style.setProperty('--popover', hexToHsl(colors.card));
        root.style.setProperty('--muted', '210 40% 96.1%');
        root.style.setProperty('--accent', '210 40% 96.1%');
        root.style.setProperty('--border', hexToHsl(colors.border));
        root.style.setProperty('--input', hexToHsl(colors.border));
        root.style.setProperty('--sidebar', hexToHsl(colors.sidebar));
        root.style.setProperty('--sidebar-accent', '220 14% 93%');
        root.style.setProperty('--sidebar-border', hexToHsl(colors.border));
        root.style.setProperty('--foreground', '222.2 84% 4.9%');
        root.style.setProperty('--card-foreground', '222.2 84% 4.9%');
        root.style.setProperty('--popover-foreground', '222.2 84% 4.9%');
        root.style.setProperty('--muted-foreground', '215.4 16.3% 46.9%');
        root.style.setProperty('--accent-foreground', '222.2 47.4% 11.2%');
        root.style.setProperty('--sidebar-foreground', '220 9% 46%');
        root.style.setProperty('--sidebar-accent-foreground', '220 9% 20%');
    }

    // Primary color
    const primaryColor = themePrefs.primaryColor || tenantTheme.primaryColor;
    if (primaryColor) {
        const primaryHsl = hexToHsl(primaryColor);
        root.style.setProperty('--primary', primaryHsl);
        root.style.setProperty('--primary-foreground', getContrastColor(primaryColor));
        root.style.setProperty('--ring', primaryHsl);
    }

    // Secondary color
    const secondaryColor = themePrefs.secondaryColor || tenantTheme.secondaryColor;
    if (secondaryColor) {
        root.style.setProperty('--secondary', hexToHsl(secondaryColor));
        root.style.setProperty('--secondary-foreground', getContrastColor(secondaryColor));
    }

    // Global border radius
    const borderRadius = themePrefs.borderRadius ?? 0.5;
    root.style.setProperty('--radius', `${borderRadius}rem`);

    // Tooltip CSS vars
    if (themePrefs.tooltips) {
        const lt = themePrefs.tooltips.lightTheme;
        if (lt) {
            root.style.setProperty('--tooltip-light-bg', lt.backgroundColor || '#CBD5E1');
            root.style.setProperty('--tooltip-light-text', lt.textColor || '#1E293B');
            root.style.setProperty('--tooltip-light-title-bg', lt.titleBackgroundColor || '#F8FAFC');
            root.style.setProperty('--tooltip-light-title-text', lt.titleTextColor || '#0F172A');
            root.style.setProperty('--tooltip-light-border-radius', `${lt.borderRadius || 0.5}rem`);
        }
        const dt = themePrefs.tooltips.darkTheme;
        if (dt) {
            root.style.setProperty('--tooltip-dark-bg', dt.backgroundColor || '#06162D');
            root.style.setProperty('--tooltip-dark-text', dt.textColor || '#F1F5F9');
            root.style.setProperty('--tooltip-dark-title-bg', dt.titleBackgroundColor || '#1E293B');
            root.style.setProperty('--tooltip-dark-title-text', dt.titleTextColor || '#F8FAFC');
            root.style.setProperty('--tooltip-dark-border-radius', `${dt.borderRadius || 0.5}rem`);
        }
    }

    // Notifications CSS vars
    if (themePrefs.notifications) {
        const notif = actualMode === 'dark' 
            ? themePrefs.notifications.darkTheme 
            : themePrefs.notifications.lightTheme;
            
        if (notif) {
            if (notif.successColor) root.style.setProperty('--toast-success-rgb', hexToRgbComma(notif.successColor));
            if (notif.errorColor) root.style.setProperty('--toast-error-rgb', hexToRgbComma(notif.errorColor));
            if (notif.warningColor) root.style.setProperty('--toast-warning-rgb', hexToRgbComma(notif.warningColor));
            if (notif.infoColor) root.style.setProperty('--toast-info-rgb', hexToRgbComma(notif.infoColor));
        }
    }
}
